import React from 'react';
import { useTranslation } from 'react-i18next';

import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TransferPanel } from '@/components/ui/panels/TransferPanel';
import { PermissionGuard } from '@/components/layout';
import { useAssignEmployeesTab } from '@/hooks/securityGrouping';


export const AssignEmployeesTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    groupingOptions,
    selectedGroupId,
    onGroupChange,
    available,
    filteredAvailable,
    isAvailableLoading,
    hasMoreAvailable,
    totalAvailable,
    availableSearchText,
    onAvailableSearch,
    availableSentinelRef,
    availableScrollContainerRef,
    assigned,
    filteredAssigned,
    isAssignedLoading,
    hasMoreAssigned,
    totalAssigned,
    assignedSearchText,
    onAssignedSearch,
    assignedSentinelRef,
    assignedScrollContainerRef,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickAvailable,
    onDoubleClickAssigned,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    canEdit,
    onSave,
    onCancel,
  } = useAssignEmployeesTab();

  return (
    <div className="flex flex-col gap-6" data-testid="assign-employees-tab">
      <div className="w-96">
        <Selector
          label={translate('securityGrouping:assignEmployees.groupingLabel')}
          placeholder={translate('securityGrouping:assignEmployees.groupingPlaceholder')}
          options={groupingOptions}
          value={selectedGroupId ? String(selectedGroupId) : undefined}
          onChange={(value) => onGroupChange(value ? Number(value) : null)}
          data-testid="assign-employees-grouping-selector"
        />
      </div>

      <TransferPanel
        keyExtractor={(employee) => employee.employeeId}
        renderTitle={(employee) => employee.employeeName}
        renderSubtitle={(employee) => employee.employeeTaxIdentificationNumber}
        leftPanel={{
          title: translate('securityGrouping:assignEmployees.availableTitle'),
          items: filteredAvailable,
          allItems: available,
          isLoading: isAvailableLoading,
          totalRecords: totalAvailable,
          searchValue: availableSearchText,
          onSearch: onAvailableSearch,
          searchPlaceholder: translate('securityGrouping:assignEmployees.search'),
          selectedIds: selectedAvailableIds,
          onToggleSelect: onToggleAvailable,
          onSelectAll: onSelectAllAvailable,
          isAllSelected: isAllAvailableSelected,
          isIndeterminate: isAvailableIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAvailable : () => {},
          onBulkAction: (isRemoveAllPending || !canEdit) ? undefined : onAddAllFromServer,
          bulkActionMessage: translate(
            'securityGrouping:assignEmployees.addAllBannerMessage',
            { total: totalAvailable },
          ),
          sentinelRef: availableSentinelRef,
          scrollContainerRef: availableScrollContainerRef,
          selectAllHint: hasMoreAvailable
            ? translate('securityGrouping:assignEmployees.selectAllHint', { loaded: available.length })
            : undefined,
          footerText: (isRemoveAllPending && hasMoreAvailable)
            ? translate('securityGrouping:assignEmployees.footerSavePending', {
              pending: totalAvailable - available.length,
            })
            : hasMoreAvailable
              ? translate('securityGrouping:assignEmployees.footerPartial', {
                selected: selectedAvailableIds.size,
                loaded: available.length,
                total: totalAvailable,
              })
              : translate('securityGrouping:assignEmployees.footer', {
                selected: selectedAvailableIds.size,
                total: available.length,
              }),
          'data-testid': 'available-employees-panel',
        }}
        rightPanel={{
          title: translate('securityGrouping:assignEmployees.assignedTitle'),
          items: filteredAssigned,
          allItems: assigned,
          isLoading: isAssignedLoading,
          totalRecords: totalAssigned,
          searchValue: assignedSearchText,
          onSearch: onAssignedSearch,
          searchPlaceholder: translate('securityGrouping:assignEmployees.search'),
          selectedIds: selectedAssignedIds,
          onToggleSelect: onToggleAssigned,
          onSelectAll: onSelectAllAssigned,
          isAllSelected: isAllAssignedSelected,
          isIndeterminate: isAssignedIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAssigned : () => {},
          onBulkAction: (isAddAllPending || !canEdit) ? undefined : onRemoveAllFromServer,
          bulkActionMessage: translate(
            'securityGrouping:assignEmployees.removeAllBannerMessage',
            { total: totalAssigned },
          ),
          sentinelRef: assignedSentinelRef,
          scrollContainerRef: assignedScrollContainerRef,
          selectAllHint: hasMoreAssigned
            ? translate('securityGrouping:assignEmployees.selectAllHint', { loaded: assigned.length })
            : undefined,
          footerText: (isAddAllPending && hasMoreAssigned)
            ? translate('securityGrouping:assignEmployees.footerSavePending', {
              pending: totalAssigned - assigned.length,
            })
            : hasMoreAssigned
              ? translate('securityGrouping:assignEmployees.footerPartial', {
                selected: selectedAssignedIds.size,
                loaded: assigned.length,
                total: totalAssigned,
              })
              : translate('securityGrouping:assignEmployees.footer', {
                selected: selectedAssignedIds.size,
                total: assigned.length,
              }),
          'data-testid': 'assigned-employees-panel',
        }}
        onAdd={onAdd}
        onRemove={onRemove}
        addLabel={translate('securityGrouping:assignEmployees.addButton')}
        removeLabel={translate('securityGrouping:assignEmployees.removeButton')}
        addAllLabel={translate('securityGrouping:assignEmployees.addAll')}
        removeAllLabel={translate('securityGrouping:assignEmployees.removeAll')}
        isAddDisabled={selectedAvailableIds.size === 0 || !canEdit}
        isRemoveDisabled={selectedAssignedIds.size === 0 || !canEdit}
        data-testid="assign-employees-transfer"
      />

      <div className="flex justify-center gap-3 pt-2">
        <PrimaryButton
          variant="outline"
          onClick={onCancel}
          disabled={!isDirty || isSaving}
          data-testid="assign-employees-cancel-button"
        >
          {translate('securityGrouping:assignEmployees.cancel')}
        </PrimaryButton>
        <PermissionGuard action="edit">
          <PrimaryButton
            onClick={() => { void onSave(); }}
            isLoading={isSaving}
            disabled={!isDirty || isSaving}
            data-testid="assign-employees-save-button"
          >
            {translate('securityGrouping:assignEmployees.saveChanges')}
          </PrimaryButton>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default AssignEmployeesTab;
